import React, {useEffect, useState, useRef} from "react";
import {
    VsiBubble,
    VsiH4,
    VsiH5, VsiH6,
    VsiMedText,
    VsiSmallText
} from "../../../styled/App.styled";
import { useVsiAppContext} from "../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import { useAuthWithRoute, useDataWithRoute, useDebounce, useTitleFromComponent } from "../../../utils/hooks";
import {
    Badge,
    Button,
    ButtonGroup,
    Card,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
    Toast,
    ToastBody,
    ToastHeader
} from "reactstrap";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import Stockchart from "../../../components/Stockchart";
import {
    forecastWithRateV2,
    formatDualAxisStockChartConfig,
    formatStockChartConfig
} from "../../../utils/Helpers";
import {iopsLineColor, latencyLineColor} from "../../../utils/chartUtils";
import VsiCreatableSelect from "../../../components/VsiCreatableSelect";
import {DeleteAuth, PostAuth, PutAuth} from "../../../utils/Verbs";
import dayjs from "dayjs";
import themeStyles from "../../../utils/themeStyles";

const chartHeight = 450;

const dangerUtil = 90;
const warningUtil = 70;

const perfDefs = [
    {
        name: 'IOPS',
        field: 'iops',
        yAxis: 0
    },
    {
        name: 'Threshold',
        field: 'perf_threshold',
        yAxis: 0
    },
    {
        name: 'Latency',
        field: 'latency',
        yAxis: 1
    },
];
const perfConfig = {
    yAxis: [
        {
            title: {
                text: 'IOPS',
            },
            opposite: false,

        },
        {
            title: {
                text: 'Latency (ms)',
            },
            opposite: true
        }
    ],
    animation: false,
    colors: [iopsLineColor, 'gray', latencyLineColor],
};

const capDefs = [
    {
        type: "areaspline",
        name: 'Effective Used (Host)',
        field: 'host_used',
        zIndex: 2
    },
    {
        type: "areaspline",
        name: 'Effective Free (Device)',
        field: 'effective_free',
    },
]
const capConfig = {
    yAxis: {
        title: {
            text: 'GiB'
        },
        animation: false,
        reversedStacks: false,
    },
}

const SidePerformanceCategories = ({theme, host}) => {
    const renderLunSummary = host => {
        const allKeys = [
            {
                name: 'Fibre',
                field: 'fibre_percent',
            },
            {
                name: 'SSD',
                field: 'ssd_percent',
            },

            {
                name: 'SATA',
                field: 'sata_percent',
            }
        ];


        return (
            <>
                {allKeys.every(key => host[key.field] !== undefined) ? allKeys.map((key, index) => (
                    <Badge key={`${encodeURI(key.field)}-${index}`}
                           color={host[key.field] >= dangerUtil ? 'danger' : host[key.field] >= warningUtil ? 'warning' : 'info'}>
                        {key.name}: {`${Math.round(host[key.field])}%`}
                    </Badge>
                )) : <VsiMedText>N/A</VsiMedText>}
            </>
        )
    }

    return (
        <div className={"d-flex flex-row"}>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Array Disk Type:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    <VsiMedText>{(host?.disk_type || host?.device_type) ?? 'N/A'}</VsiMedText>
                </ToastBody>
            </Toast>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Host LUN Count:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    <VsiMedText>{host?.lun_count ?? 'N/A'}</VsiMedText>
                </ToastBody>
            </Toast>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        LUN Summary:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    {renderLunSummary(host)}
                </ToastBody>
            </Toast>
        </div>
    )
}

const SideCapacityCategories = ({theme, host}) => {
    const arrayUtil = (host?.utilization * 100);
    const hostUsed = host.total_alloc - host.total_free;
    const hostUtil = parseFloat((hostUsed / (host.total_alloc || 1) * 100)).toFixed(2);
    return (
        <div className={"d-flex flex-row align-items-center"}>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Array Utilization:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    {!isNaN(arrayUtil) ? (
                        <Badge className={'me-1'}
                               color={arrayUtil >= dangerUtil ? 'danger' : arrayUtil >= warningUtil ? 'warning' : 'info'}>
                            {`${arrayUtil?.toLocaleString?.()}%`}
                        </Badge>
                    ) : (
                        <VsiMedText>N/A</VsiMedText>
                    )}
                </ToastBody>
            </Toast>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Deduplication
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    <VsiMedText>{host?.['data_reduction_ratio']?.toLocaleString?.() ? host?.['data_reduction_ratio']?.toLocaleString?.() + ' : 1' : 'N/A'}</VsiMedText>
                </ToastBody>
            </Toast>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Host Utilization:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    {!isNaN(hostUtil) ? (
                        <>
                            <Badge className={'me-1'}
                                   color={hostUtil >= dangerUtil ? 'danger' : hostUtil >= warningUtil ? 'warning' : 'info'}>
                                {`${hostUtil?.toLocaleString?.()}%`}
                            </Badge>
                            <VsiSmallText>({hostUsed?.toLocaleString?.()} GiB)</VsiSmallText>
                        </>
                    ) : (
                        <VsiMedText>N/A</VsiMedText>
                    )}
                </ToastBody>
            </Toast>
        </div>
    )
}

export default function StorageHostDeviceCapacityModeling({context, title}) {
    useTitleFromComponent(`${title}`);

    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const [allSavedModels, {
        isLoading: isAllSavedModelsLoading,
        reFetchData: reFetchModels
    }] = useAuthWithRoute(`/api/v6/admin/host_device_capacity_modeling/${client.clientId}`);
    const [allSavedModelsOptions, setAllSavedModelsOptions] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);

    const [allDevicesWithHosts, {isLoading: isAllDevicesWithHostsLoading}] = useDataWithRoute(`/api/v6/report/enterprise_devices_with_hosts_proc?client_id=${client.clientId}&period_id=${periodId}`, !isAllSavedModelsLoading);
    const [allDevicesWithHostsOptions, setAllDevicesWithHostsOptions] = useState([]);

    const [allResourceGroups, {isLoading: isAllResourceGroupsLoading}] = useDataWithRoute(`/api/v6/report/cloud_all_resouce_groups_proc?client_id=${client.clientId}`);
    const [allDevices, {isLoading: isAllStorageDevicesLoading}] = useDataWithRoute(`/api/v6/report/enterprise_storage_all_devices_proc?client_id=${client.clientId}&period_id=${periodId}`);

    const isAllDevicesLoading = isAllResourceGroupsLoading && isAllStorageDevicesLoading;

    const [allDevicesOptions, setAllDevicesOptions] = useState([]);

    const [currentDeviceSelected, setCurrentDeviceSelected] = useState(null);
    const [currentHostSelected, setCurrentHostSelected] = useState(null);

    const [allCurrentHosts, {isLoading: isAllCurrentHostsLoading}] = useDataWithRoute(`/api/v6/report/enterprise_host_capacity_modelling_list_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${encodeURI(currentDeviceSelected?.value)}`, (!isAllSavedModelsLoading && Boolean(currentDeviceSelected?.value)));
    const [allCurrentHostsOptions, setAllCurrentHostsOptions] = useState([]);

    const [modeledDeviceSelected, setModeledDeviceSelected] = useState(null);

    const [currentDeviceHistory, { isLoading: isHistoryLoading }] = useDataWithRoute(`/api/v6/report/host_history_proc?client_id=${client.clientId}&device_name=${encodeURI(currentDeviceSelected?.value)}&host_name=${encodeURI(currentHostSelected?.value)}&period_id=${periodId}`, (!isAllSavedModelsLoading && Boolean(currentDeviceSelected?.value && currentHostSelected?.value)));

    const [currentPerfTrendData, setCurrentPerfTrendData] = useState([]);
    const [modeledPerfTrendData, setModeledPerfTrendData] = useState([]);

    const [currentCapTrendData, setCurrentCapTrendData] = useState([]);
    const [modeledCapTrendData, setModeledCapTrendData] = useState([]);

    const [maxes, setMaxes] = useState({});
    const [mins, setMins] = useState({});
    const [isAxisMaxStandard, setIsAxisMaxStandard] = useState(localStorage.getItem("vsi-host-modeler-axis-maximum") === "true");

    const debouncedIsMaxStandard = useDebounce(isAxisMaxStandard, 250);

    const titleDivRef = useRef(null); // used for ensuring titles are same height (this is so brilliant holy moly I love React)

    const setCurrentDevicesFormat = (currentDeviceParam) => {
        const allDevicesOptionsNew = (allDevicesWithHosts?.recordset?.map?.(device => ({
            ...device,
            value: device.device_name,
            label: device.device_name,
        })) ?? []).filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t.value === value.value
                ))
        );

        let currentDeviceFound = null;

        if (currentDeviceParam) {
            currentDeviceFound = allDevicesOptionsNew?.find?.(device => device.value === currentDeviceParam) ?? currentDeviceFound;
        }

        setAllDevicesWithHostsOptions(allDevicesOptionsNew);

        if (currentDeviceFound) {
            setCurrentDeviceSelected(currentDeviceFound);
        }

    }

    const setModeledDevicesFormat = (modeledDeviceParam) => {
        const allDevicesOptionsNew = ([...(allDevices?.recordset ?? []), ...(allResourceGroups?.recordset ?? [])]?.map?.(device => ({
            ...device,
            value: device.device_name,
            label: device.device_name,
        })) ?? []).filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t.value === value.value
                ))
        );

        let modeledDeviceFound = null;

        if (modeledDeviceParam) {
            modeledDeviceFound = allDevicesOptionsNew?.find?.(device => device.value === modeledDeviceParam) ?? modeledDeviceFound;
        }

        setAllDevicesOptions(allDevicesOptionsNew);
        if (modeledDeviceFound) {
            setModeledDeviceSelected(modeledDeviceFound);
        }
    }

    const setCurrentHostFormat = (currentHostParam) => {
        const allCurrentHostOptions = allCurrentHosts?.recordsets?.[0]?.map?.(host => ({
            ...host,
            value: host.host_name,
            label: host.host_name,
        })) ?? [];

        let currentHostFound = null;

        if (currentHostParam) {
            currentHostFound = allCurrentHostOptions?.find?.(host => host.value === currentHostParam);
        }

        setAllCurrentHostsOptions(allCurrentHostOptions);
        if (currentHostFound) {
            setCurrentHostSelected(currentHostFound);
        }
    }

    const setSavedModelFormat = () => {
        const foundModel = searchParams.get('model_name');
        const allOptions = allSavedModels.map(model => ({
            ...model,
            label: model.projectName,
            value: model.projectName,
        }));

        setAllSavedModelsOptions(allOptions);
        if (foundModel) {
            setSelectedModel(allOptions.find(model => model.label = decodeURIComponent(foundModel)));
        }

    }

    const setAllChartData = () => {
        const sortedData = [...(currentDeviceHistory?.recordset ?? [])]?.sort?.((a, b) => Date.parse(a.collection_date) - Date.parse(b.collection_date));
        const newCurrentPerfTrend = formatDualAxisStockChartConfig(sortedData ?? [], perfDefs);
        let maxIops = 0;
        let maxLatency = 0;
        let maxGib = 0;
        let minIops = 0;
        let minLatency = 0;
        let minGib = 0;

        const modeledTrend = forecastWithRateV2(sortedData, ['iops', 'latency', 'host_used', 'effective_free'], 7).map(datum => ({
            ...datum,
            perf_threshold: (modeledDeviceSelected?.perf_threshold ?? 0) - datum.iops,
            effective_free: datum.effective_free - datum.host_used,
        }));

        const allData = [...sortedData, ...modeledTrend];
        allData.forEach(datum => {
            const thisMaxGib = Math.max(datum.host_used, datum.effective_free);
            const thisMinGib = Math.min(datum.host_used, datum.effective_free);
            if (datum.iops > maxIops) {
                maxIops = datum.iops;
            }
            if (datum.latency > maxLatency) {
                maxLatency = datum.latency;
            }
            if (thisMaxGib > maxGib) {
                maxGib = thisMaxGib;
            }

            if (datum.iops < minIops) {
                minIops = datum.iops;
            }
            if (datum.latency < minLatency) {
                minLatency = datum.latency;
            }
            if (thisMinGib < minGib) {
                minGib = thisMinGib;
            }
        });

        const newModeledPerfTrend = formatDualAxisStockChartConfig(modeledTrend, perfDefs);

        setCurrentPerfTrendData(newCurrentPerfTrend.series);
        setModeledPerfTrendData(newModeledPerfTrend.series.map(series => ({
            ...series,
            zoneAxis: "x",
            zones: [
              {
                value: Date.parse(
                  sortedData?.[sortedData.length - 1]?.collection_date?.split?.("T")?.[0]
                ),
              },
              {
                dashStyle: "Dash",
              },
            ],
        })));

        const newCurrentCapTrend = formatStockChartConfig(sortedData, capDefs);
        const newModeledCapTrend = formatStockChartConfig(modeledTrend, capDefs);

        setCurrentCapTrendData(newCurrentCapTrend.series.map(series => ({
            ...series,
            stacking: "normal",
        })));

        setModeledCapTrendData(newModeledCapTrend.series.map(series => ({
            ...series,
            zoneAxis: "x",
            stacking: "normal",
            zones: [
              {
                value: Date.parse(
                  sortedData?.[sortedData.length - 1]?.collection_date?.split?.("T")?.[0]
                ),
              },
              {
                dashStyle: "Dash",
              },
            ],
        })));

        setMaxes({
            iops: maxIops,
            latency: maxLatency,
            gib: maxGib,
        });
        setMins({
            iops: minIops,
            latency: minLatency,
            gib: minGib,
        });
    }

    useEffect(() => {
        setCurrentDevicesFormat(searchParams.get('current_device'));
        setModeledDevicesFormat(searchParams.get('modeled_device'));
    }, [allDevices, allDevicesWithHosts, allResourceGroups]);

    useEffect(() => {
        setCurrentHostFormat(searchParams.get('current_host'));
    }, [allCurrentHosts]);

    useEffect(() => {
        setSavedModelFormat();
    }, [allSavedModels]);

    useEffect(() => {
        if (selectedModel?.currentHost && decodeURIComponent(searchParams.get('current_host')) !== selectedModel?.currentHost) {
            setCurrentHostSelected(allCurrentHostsOptions.find(host => host.value === selectedModel.currentHost));
        }
    }, [allCurrentHostsOptions, selectedModel]);


    useEffect(() => {
        setAllChartData();
    }, [currentDeviceHistory, currentDeviceSelected, modeledDeviceSelected]);

    useEffect(() => {
        if (currentDeviceSelected?.value && currentHostSelected?.value && !modeledDeviceSelected?.value && allDevicesOptions.length) {
            setModeledDeviceSelected(allDevicesOptions[0]);
        }
    }, [currentHostSelected, currentDeviceSelected, modeledDeviceSelected, allDevicesOptions]);

    const clearSelectedModel = () => {
        setSelectedModel(null);
        searchParams.delete('model_name');
        setSearchParams(searchParams);
    }


    const onCurrentDeviceChange = device => {
        clearSelectedModel();
        searchParams.set('current_device', encodeURIComponent(device.value));
        searchParams.delete('current_host');
        searchParams.delete('modeled_device');
        setSearchParams(searchParams, {replace: true});
        setCurrentHostSelected(null);
        setModeledDeviceSelected(null);
        setCurrentDeviceSelected(device);
    }

    const onCurrentHostChange = host => {
        clearSelectedModel();
        searchParams.set('current_host', encodeURIComponent(host.value));
        setSearchParams(searchParams, {replace: true});
        setCurrentHostSelected(host);
    }

    const onModeledDeviceChange = device => {
        clearSelectedModel();
        searchParams.set('modeled_device', encodeURIComponent(device.value));
        setSearchParams(searchParams, {replace: true});
        setModeledDeviceSelected(device);
    }

    const onModelCreateOption = val => {
        const newOption = {
            label: val,
            value: val,
        };
        setAllSavedModelsOptions(prev => [...prev, newOption]);
        setSelectedModel(newOption);
    }

    const onModelChange = model => {
        setSelectedModel(model);
        searchParams.set('model_name', encodeURIComponent(model?.label));
        setSearchParams(searchParams);
        setCurrentDeviceSelected(allDevicesWithHostsOptions.find(device => device.label === model.currentDevice));
        setModeledDeviceSelected(allDevicesOptions.find(device => device.label === model.modeledDevice));
    }

    const determineModelName = () => {
        if (selectedModel?.label) return selectedModel.label;
        else {
            let label = `${dayjs().format('MM/DD/YY')} Model`;
            let duplicate = allSavedModelsOptions.find(model => model.label === label);
            if (duplicate) {
                const allLikeModels = [...allSavedModelsOptions].filter(model => model?.label?.split('(')?.[0] === label);
                const allIterations = allLikeModels.map(model => {
                    let num = model?.label?.split('(')?.[1]?.replace?.(')', '');
                    return parseInt(num);
                }).sort((a, b) => a - b);
                const num = allIterations[allIterations.length - 1] + 1;

                label += `(${isNaN(num) ? 1 : num})`
            }
            return label;
        }
    }

    const onSaveClick = async () => {
        try {
            const plan = {
                clientId: client.clientId,
                notes: '',
                projectName: determineModelName(),
                currentDevice: currentDeviceSelected.label,
                currentHost: currentHostSelected.label,
                modeledDevice: modeledDeviceSelected.label,
            };
            const token = await getAccessTokenSilently();
            if (!selectedModel?._id) {
                const postData = await PostAuth(`/api/v6/admin/host_device_capacity_modeling`, plan, token);
                setSelectedModel({
                    ...postData?.data,
                    label: plan.projectName,
                    value: plan.projectName,
                });
            } else {
                const putData = await PutAuth(`/api/v6/admin/host_device_capacity_modeling/${selectedModel._id}`, plan, token);
                setSelectedModel({
                    ...putData?.data,
                    label: plan.projectName,
                    value: plan.projectName,
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    const onDeleteClick = async () => {
        const planId = selectedModel?._id;
        const token = await getAccessTokenSilently();
        if (planId) {
            try {
                await DeleteAuth(`/api/v6/admin/host_device_capacity_modeling/${planId}`, token);
                clearSelectedModel()
                reFetchModels();
            } catch (error) {
                console.error(error);
            }
        }
    }

    const onSwitchAxisMaxClick = () => {
        setIsAxisMaxStandard(prev => {
            localStorage.setItem("vsi-host-modeler-axis-maximum", !prev);
            return !prev;
        });
    }

    const isGlobalHistoryLoading = isHistoryLoading && currentHostSelected?.value && currentDeviceSelected?.value;

    return (
      <>
          <Card className={`report-card p-2 nav-card-${theme} mb-2`}>
            <div className={"d-flex justify-content-between"}>
                <VsiH4>Host & Device Capacity Modeling</VsiH4>
            </div>
            <VsiBubble noAutobot>
                <div className={'d-flex flex-row align-items-end'}>
                    <VsiCreatableSelect options={allSavedModelsOptions}
                                        isDisabled={isAllSavedModelsLoading}
                                        isLoading={isAllSavedModelsLoading}
                                        label={'Saved/save new model:'}
                                        minWidth={350}
                                        placeholder={'Type a project name and press enter'}
                                        onChange={onModelChange}
                                        onCreateOption={onModelCreateOption}
                                        defaultValue={selectedModel}/>
                    <ButtonGroup className={"mb-2"}>
                        <Button color={'danger'}
                                disabled={(!selectedModel?._id || isAllDevicesLoading || isAllSavedModelsLoading || isAllCurrentHostsLoading || isAllDevicesWithHostsLoading)}
                                onClick={onDeleteClick}>
                            <i className="fa-solid fa-trash me-2"/>
                            <span>Delete</span>
                        </Button>
                        <Button color={'success'} onClick={onSaveClick}>
                            <i className="fa-solid fa-floppy-disk me-2"/>
                            <span>Save</span>
                        </Button>
                    </ButtonGroup>
                </div>
            </VsiBubble>
          </Card>
        <Row>
            <Col xs={12} lg={6}>
                <Card className={`report-card p-2 nav-card-${theme}`}>
                    <div className={"d-flex justify-content-between"}
                         style={{minHeight: titleDivRef?.current?.scrollHeight ?? null}}>
                        <VsiH5 style={{fontStyle: 'italic'}}>Current History</VsiH5>
                    </div>
                    <VsiBubble noAutobot>
                        <div className={"d-flex"}>
                            <VsiLabeledSelect options={allDevicesWithHostsOptions}
                                              isDisabled={isAllDevicesWithHostsLoading || isGlobalHistoryLoading}
                                              isLoading={isAllDevicesWithHostsLoading || isGlobalHistoryLoading}
                                              label={'Source Device:'}
                                              minWidth={'50%'}
                                              onChange={onCurrentDeviceChange}
                                              defaultValue={currentDeviceSelected}/>
                            <VsiLabeledSelect options={allCurrentHostsOptions}
                                              isDisabled={(allCurrentHostsOptions.length < 1 || isAllCurrentHostsLoading) || isGlobalHistoryLoading}
                                              isLoading={(currentDeviceSelected?.value && isAllCurrentHostsLoading) || isGlobalHistoryLoading}
                                              label={'Host:'}
                                              placeholder={currentDeviceSelected ? 'Select...' : 'Please select source device'}
                                              minWidth={'50%'}
                                              onChange={onCurrentHostChange}
                                              defaultValue={currentHostSelected}/>
                        </div>
                    </VsiBubble>
                    <Row>
                       <Col xs={12} className={"mb-1"}>
                           <VsiH6 style={{fontStyle: 'italic'}}>Performance</VsiH6>
                            <SidePerformanceCategories host={{...currentHostSelected, ...currentDeviceSelected}}
                                                       theme={theme}/>
                        </Col>
                        <Col xs={12}>
                            {/* <VsiBubble autobotName='CurrentPerformanceStockChart'> */}
                            <VsiBubble noAutobot>
                                <Stockchart height={chartHeight}
                                            animation={false}
                                            isLoading={isHistoryLoading}
                                            title={(currentDeviceSelected?.value && currentHostSelected?.value) ? `${currentHostSelected.label} performance on ${currentDeviceSelected.label}` : ''}
                                            series={currentPerfTrendData}
                                            config={{
                                                ...perfConfig,
                                                yAxis: perfConfig.yAxis.map((perf, index) => ({
                                                    ...perf,
                                                    max: debouncedIsMaxStandard ? (index === 0 ? maxes.iops : maxes.latency) : null,
                                                }))
                                            }}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} className={"mb-1"}>
                           <VsiH6 style={{fontStyle: 'italic'}}>Capacity</VsiH6>
                            <SideCapacityCategories host={{...currentHostSelected, ...currentDeviceSelected}}
                                                    theme={theme}/>
                        </Col>
                        <Col xs={12}>
                            {/* <VsiBubble autobotName='CurrentCapacityStockChart'> */}
                            <VsiBubble noAutobot>
                                <Stockchart height={chartHeight}
                                            animation={false}
                                            series={currentCapTrendData}
                                            config={{
                                                ...capConfig,
                                                yAxis: {
                                                    ...capConfig.yAxis,
                                                    max: debouncedIsMaxStandard ? maxes.gib : null,
                                                    min: debouncedIsMaxStandard ? mins.gib : null,
                                                }
                                            }}
                                            title={(currentDeviceSelected?.value && currentHostSelected?.value) ? `${currentHostSelected.label} capacity on ${currentDeviceSelected.label}` : ''}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                </Card>
            </Col>

            <Col xs={12} lg={6}>
                <Card className={`report-card p-2 nav-card-${theme} h-100`}>
                {currentDeviceSelected?.value && currentHostSelected?.value ? (
              (
                <>
                    <div className={"d-flex justify-content-between"} ref={titleDivRef}>
                        <VsiH5 style={{fontStyle: 'italic'}}>Modeled Forecast</VsiH5>
                        <FormGroup switch>
                            <Input type={"switch"} role={"switch"} checked={isAxisMaxStandard} onClick={onSwitchAxisMaxClick}/>
                            <Label check style={{ color: themeStyles[theme].textColor }}>Standardize axis min/max?</Label>
                        </FormGroup>

                    </div>
                    <VsiBubble noAutobot>
                        <div className={"d-flex justify-content-between align-items-center"}>
                            <VsiLabeledSelect options={allDevicesOptions}
                                              isDisabled={isAllDevicesLoading || isGlobalHistoryLoading}
                                              isLoading={isAllDevicesLoading || isGlobalHistoryLoading}
                                              label={'Target Device:'}
                                              minWidth={'50%'}
                                              onChange={onModeledDeviceChange}
                                              defaultValue={modeledDeviceSelected}/>
                            {!modeledDeviceSelected?.perf_threshold ? (
                                <div className={"h-100 d-flex flex-column justify-content-center"}>
                                    <VsiSmallText>* No performance threshold available</VsiSmallText>
                                </div>
                            ) : (
                              <div className={"d-flex flex-row justify-content-end align-items-end"}>
                                  <div className={"d-flex flex-column justify-content-start align-items-end"}>
                                      <VsiMedText style={{ textAlign: 'right '}}>Performance Threshold:</VsiMedText>
                                      <VsiH6 style={{ textAlign: 'right '}}>{(modeledDeviceSelected?.perf_threshold)?.toLocaleString()}</VsiH6>
                                  </div>
                              </div>
                            )}
                        </div>
                    </VsiBubble>
                    <Row>
                        <Col xs={12} className={"mb-1"}>
                            <VsiH6 style={{fontStyle: 'italic'}}>Performance</VsiH6>
                            <SidePerformanceCategories host={{...currentHostSelected, ...modeledDeviceSelected}}
                                                       theme={theme}/>
                        </Col>
                        <Col xs={12}>
                            {/* <VsiBubble autobotName='ModeledPerformanceStockChart'> */}
                            <VsiBubble noAutobot>
                                <Stockchart height={chartHeight}
                                            animation={false}
                                            title={(modeledDeviceSelected?.value && currentHostSelected?.value) ? `${currentHostSelected.label} performance on ${modeledDeviceSelected.label}` : ''}
                                            series={isHistoryLoading ? [] : modeledPerfTrendData}
                                            config={{
                                                ...perfConfig,
                                                yAxis: perfConfig.yAxis.map((perf, index) => ({
                                                    ...perf,
                                                    max: debouncedIsMaxStandard ?
                                                      index === 0 ?
                                                        maxes.iops :
                                                        maxes.latency
                                                      : null,
                                                    min: debouncedIsMaxStandard ?
                                                      index === 0 ?
                                                        mins.iops :
                                                        mins.latency
                                                      : null,
                                                }))
                                            }}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} className={"mb-1"}>
                            <VsiH6 style={{fontStyle: 'italic'}}>Capacity</VsiH6>
                            <SideCapacityCategories host={{...currentHostSelected, ...modeledDeviceSelected}}
                                                    theme={theme}/>
                        </Col>
                        <Col xs={12}>
                            {/* <VsiBubble autobotName='ModeledCapacityStockChart'> */}
                            <VsiBubble noAutobot>
                                <Stockchart height={chartHeight}
                                            animation={false}
                                            series={isHistoryLoading ? [] : modeledCapTrendData}
                                            config={{
                                                ...capConfig,
                                                yAxis: {
                                                    ...capConfig.yAxis,
                                                    max: debouncedIsMaxStandard ? maxes.gib : null,
                                                    min: debouncedIsMaxStandard ? mins.gib : null,
                                                }
                                            }}
                                            title={(modeledDeviceSelected?.value && currentHostSelected?.value) ? `${currentHostSelected.label} capacity on ${modeledDeviceSelected.label}` : ''}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                </>
              )
            ) :
                <>
                    <VsiH5 style={{fontStyle: 'italic'}}>
                        * Please select a source device and host
                    </VsiH5>
                </>}
                </Card>
            </Col>
        </Row>
          </>
    )
}
